/**
 * What the OPTIMA panel decides without drawing anything (bata-platform epic 719, ADR 9080).
 *
 * Pure functions, so the tests assert on strings and not on a rendered tree: how an answer is
 * cleaned before it is drawn, which words a failure gets, and what the report is pre-filled with.
 */

import { ApiProblemError } from '../../api';
import type { FeedbackKind } from '../feedback/api';
import type { FeedbackPrefill } from '../feedback/FeedbackForm';

import type { AssistantAction, AssistantTurn, FilingReceipt, IntakeDraft, TurnIntake } from './api';

/** The shortcut that opens and closes the panel: Alt+O. Ctrl/⌘ K is the command palette's. */
export const OPTIMA_SHORTCUT_KEY = 'KeyO';

/**
 * Whether a key press is the panel's shortcut. By physical key (`code`), not by the character:
 * on a Mac Option+O types «ø» and on a Spanish or Italian layout the letters move, but the key
 * the person means is the same one. Ctrl, ⌘ and Shift must be up, so AltGr (Ctrl+Alt on Windows)
 * and the browser's own Alt+Shift chords are left alone.
 */
export function isOptimaShortcut(event: Pick<KeyboardEvent, 'code' | 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey'>): boolean {
  return event.code === OPTIMA_SHORTCUT_KEY && event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
}

/**
 * Control characters and the bidirectional overrides that let text pretend to be something else.
 * Line breaks (LF, and CR/CRLF read as LF) and tabs are kept: they are the answer's layout.
 */
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARACTERS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F‪-‮⁦-⁩]/g;

/**
 * An answer as the panel draws it: TEXT. The platform has already stripped markup, links and
 * images; this is the second wall (defence in depth). It does not try to find or remove markup —
 * that is a parser's job and a parser is how an escape gets through. It only guarantees that
 * what is drawn is characters: React escapes them, no element is made from them, and the text
 * the person reads is the text the platform sent, line breaks preserved.
 */
export function plainText(raw: string): string {
  return raw.replace(/\r\n?/g, '\n').replace(UNSAFE_CHARACTERS, '');
}

/** Why a question did not get an answer, as far as the panel distinguishes it. */
export type AskFailure = 'quota' | 'unavailable' | 'disabled' | 'other';

/**
 * Classifies a failure by the HTTP status of the platform's problem document: 429 the quota is
 * spent, 503 the model provider cannot be reached, 404 the assistant is off. By status and not by
 * problem code, so a code the platform names differently still reaches the right words. Anything
 * else (a network failure, a 422, a 500) is {@link AskFailure | `other`} and is worded by
 * `useProblemMessage`, which the panel keeps for it.
 */
export function classifyFailure(error: unknown): AskFailure {
  if (!(error instanceof ApiProblemError)) return 'other';
  switch (error.status) {
    case 429: return 'quota';
    case 503: return 'unavailable';
    case 404: return 'disabled';
    default: return 'other';
  }
}

/** The catalogue key for each classified failure; `other` is worded from the problem itself. */
export const FAILURE_KEY: Readonly<Record<Exclude<AskFailure, 'other'>, string>> = {
  quota: 'optima.error.quota',
  unavailable: 'optima.error.unavailable',
  disabled: 'optima.error.disabled',
};

/** Whether the panel offers «Report a problem» under an answer: the assistant sent the person there, or suggests it. */
export function offersHandoff(turn: Pick<AssistantTurn, 'action' | 'handoff'>): boolean {
  return turn.action === 'handoff' || turn.handoff.suggested;
}

/** Which actions mean the answer is not an explanation, and say so under it. */
export function isDecline(action: AssistantAction): boolean {
  return action === 'not-documented' || action === 'refuse';
}

/** An answer whose numbers the platform could not verify is not drawn at all (epic boundary 3). */
export function isWithheld(turn: Pick<AssistantTurn, 'gate'>): boolean {
  return turn.gate.numbers === 'blocked';
}

/** One question and what came of it. */
export interface Exchange {
  /** Local to the page load; never sent. */
  readonly id: number;
  readonly question: string;
  readonly state: 'pending' | 'answered' | 'failed';
  readonly turn?: AssistantTurn;
  /** The failure, for `classifyFailure` / `useProblemMessage`. */
  readonly error?: unknown;
}

// ── the request intake (bata-platform ADR 2137, ADR 9071) ───────────────────────────────────

/**
 * The problem codes of a turn refused because its conversation is over: closed, expired or full
 * (409), or no longer the platform's (404 — pruned, or never this person's).
 */
export const CONVERSATION_OVER: ReadonlySet<string> = new Set([
  'platform.assistant.conversation_closed',
  'platform.assistant.conversation_not_found',
]);

/**
 * Whether a failed turn failed because the conversation is over: the next question opens a new one.
 * Checked by code before the status, because a 404 here is not «the assistant is off».
 */
export function isConversationClosed(error: unknown): boolean {
  return error instanceof ApiProblemError && CONVERSATION_OVER.has(error.code);
}

/**
 * The report kind a request kind is filed as. The wire keeps `incident` and `enhancement` (a defect
 * and an evolution); a platform that already sends the report's own code is read as it is.
 * `none` — not a request — is no report.
 */
export function reportKindOf(kind: string): FeedbackKind | null {
  switch (kind) {
    case 'defect': case 'incident': return 'incident';
    case 'evolution': case 'enhancement': return 'enhancement';
    case 'clarification': return 'clarification';
    case 'data': return 'data';
    default: return null;
  }
}

/** The intake a turn carries, when it carries a draft. */
export function draftOf(turn: AssistantTurn | undefined): (TurnIntake & { readonly draft: IntakeDraft }) | null {
  const intake = turn?.intake;
  if (intake === undefined || intake === null || intake.draft === null) return null;
  return { ...intake, draft: intake.draft };
}

/**
 * The exchange whose draft is the current one: the newest answered exchange that carries a draft.
 * Only its card is drawn — an earlier card is an earlier revision, and its confirmation, if any, is
 * already superseded on the platform.
 */
export function currentDraftExchange(exchanges: readonly Exchange[]): number | null {
  for (let index = exchanges.length - 1; index >= 0; index -= 1) {
    const exchange = exchanges[index];
    if (exchange !== undefined && exchange.state === 'answered' && draftOf(exchange.turn) !== null) return exchange.id;
  }
  return null;
}

/** The report form, pre-filled with every field of the draft, its kind and the conversation. */
export function draftPrefill(draft: IntakeDraft, conversationId: string | null): FeedbackPrefill {
  const kind = reportKindOf(draft.kind);
  const text = (value: string): string | undefined => (value.trim() === '' ? undefined : value);
  const items = (values: readonly string[]): readonly string[] | undefined => {
    const kept = values.filter((value) => value.trim() !== '');
    return kept.length === 0 ? undefined : kept;
  };
  const entries: Partial<Record<keyof FeedbackPrefill, unknown>> = {
    kind: kind ?? undefined,
    title: text(draft.title),
    description: text(draft.requirement),
    examples: text(draft.examples),
    sectionIds: items(draft.screens),
    markets: items(draft.markets),
    runs: items(draft.runs),
    expectedFigures: text(draft.expected_figures),
    expectedFiguresNotApplicableReason: text(draft.expected_figures_not_applicable_reason),
    acceptanceCriteria: items(draft.acceptance_criteria),
    steps: text(draft.steps),
    assistantConversationId: conversationId ?? undefined,
  };
  // `exactOptionalPropertyTypes`: an absent field is left out, never set to `undefined`.
  return Object.fromEntries(Object.entries(entries).filter(([, value]) => value !== undefined)) as FeedbackPrefill;
}

/** What became of «Sì, invia la segnalazione» on a draft. */
export type Filing =
  | { readonly state: 'sending' }
  | { readonly state: 'filed'; readonly receipt: FilingReceipt }
  | { readonly state: 'failed'; readonly error: unknown };

/** Why a filing failed, as far as the panel words it itself: the draft changed or expired (409). */
export function isDraftSuperseded(error: unknown): boolean {
  return error instanceof ApiProblemError && error.status === 409;
}

// ── the avatar, «La scatola» (the design team's handoff; ADR 9077) ──────────────────────────

/** What OPTIMA is doing, as the avatar draws it. `vuota` (the open, empty box) is not one of these: it is the entry in the bar. */
export type OptimaState = 'pronto' | 'ascolto' | 'leggo' | 'rispondo' | 'nonso' | 'bozza';

/** What the avatar can draw: the six states and the open, empty box. */
export type OptimaAvatarState = OptimaState | 'vuota';

/** The catalogue key of each state's name: the picture's `aria-label`. */
export const OPTIMA_STATE_KEYS: Readonly<Record<OptimaAvatarState, string>> = {
  pronto: 'optima.state.pronto',
  ascolto: 'optima.state.ascolto',
  leggo: 'optima.state.leggo',
  rispondo: 'optima.state.rispondo',
  nonso: 'optima.state.nonso',
  bozza: 'optima.state.bozza',
  vuota: 'optima.state.vuota',
};

/** What the live region says when a state begins, and what the header's status line says while it lasts. `null`: nothing to say. */
export const OPTIMA_ANNOUNCE_KEYS: Readonly<Record<OptimaState, string | null>> = {
  pronto: null,
  ascolto: 'optima.announce.ascolto',
  leggo: 'optima.announce.leggo',
  rispondo: 'optima.announce.rispondo',
  nonso: 'optima.announce.nonso',
  bozza: 'optima.announce.bozza',
};

/** The header's status line, under the name, in the two states that last while OPTIMA works. */
export const OPTIMA_STATUS_LINE_KEYS: Readonly<Partial<Record<OptimaState, string>>> = {
  leggo: 'optima.status.leggo',
  rispondo: 'optima.status.rispondo',
};

/** The empty panel's example questions: real questions planners asked (client feedback), filling the box when pressed. */
export const OPTIMA_EXAMPLE_KEYS: readonly string[] = [
  'optima.example.noProposal',
  'optima.example.emptySimulation',
  'optima.example.presentationMinimum',
];

/** Back to the box this long after a report has been filed (LEGGIMI: «600 ms dopo l'invio della richiesta»). */
export const RETURN_TO_BOX_MS = 600;

/**
 * How an answer ended, for the avatar beside it: `nonso` when OPTIMA did not have it (not
 * documented, refused, or withheld because a number could not be checked), `bozza` when it
 * carries the report waiting for «Sì, invia la segnalazione», `pronto` otherwise.
 */
export function answerEndState(
  turn: AssistantTurn | undefined,
  awaitingConfirmation: boolean,
): Extract<OptimaState, 'pronto' | 'nonso' | 'bozza'> {
  if (turn === undefined) return 'pronto';
  if (awaitingConfirmation) return 'bozza';
  if (isWithheld(turn) || isDecline(turn.action)) return 'nonso';
  return 'pronto';
}

/** Whether an exchange's draft is the one waiting for the person's «Sì, invia la segnalazione». */
export function awaitsConfirmation(exchange: Exchange, currentDraft: number | null, filing: Filing | undefined): boolean {
  if (exchange.state !== 'answered' || currentDraft !== exchange.id) return false;
  const confirmation = draftOf(exchange.turn)?.confirmation;
  if (confirmation === undefined || confirmation === null) return false;
  return filing?.state !== 'filed';
}

export interface AvatarInputs {
  readonly open: boolean;
  readonly exchanges: readonly Exchange[];
  readonly filings: Readonly<Record<number, Filing>>;
  /** The report was filed and {@link RETURN_TO_BOX_MS} have passed: the shoe is back in the box. */
  readonly returned: boolean;
}

/**
 * The state the panel's avatar is in, from the real lifecycle of the conversation:
 *
 *   · the panel is closed                                  → `pronto`
 *   · a question is in flight                              → `leggo` (`rispondo` needs to tell
 *     writing from reading, and the platform answers a turn in one piece: there is no stream, so
 *     the whole wait is `leggo` and nothing here pretends otherwise)
 *   · the newest answer is not-documented / refused / withheld → `nonso`
 *   · the newest answer carries a draft awaiting «Sì, invia la segnalazione» → `bozza`
 *   · the draft was filed: `ascolto`, and `pronto` once {@link RETURN_TO_BOX_MS} have passed
 *   · otherwise (the panel is open and nothing is in flight, or an answer has finished) → `ascolto`
 */
export function avatarStateOf({ open, exchanges, filings, returned }: AvatarInputs): OptimaState {
  if (!open) return 'pronto';
  if (exchanges.some((e) => e.state === 'pending')) return 'leggo';
  const newest = exchanges[exchanges.length - 1];
  if (newest === undefined || newest.state !== 'answered') return 'ascolto';
  const currentDraft = currentDraftExchange(exchanges);
  const filing = filings[newest.id];
  const ended = answerEndState(newest.turn, awaitsConfirmation(newest, currentDraft, filing));
  if (ended === 'nonso' || ended === 'bozza') return ended;
  if (currentDraft === newest.id && filing?.state === 'filed') return returned ? 'pronto' : 'ascolto';
  return 'ascolto';
}
